import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import {
  DateInput,
  FormattingContext,
  FormattingOverrides,
  TenantCultureDefaults,
  formatCurrency,
  formatCustom,
  formatDate,
  formatDateTime,
  formatDateRange,
  formatNumber,
  formatPercent,
  formatTime,
  formatTimeInputValue,
  formatTimeRange,
  localHourFraction,
  localTimeZone,
  resolveFormattingContext,
  wallClockToUtcIso,
} from '@zag/core/common';
import { LanguageService } from '../services/language.service';

/**
 * Injectable Angular service for locale-aware formatting.
 *
 * Resolves the active FormattingContext from the current language signal
 * (via LanguageService) and optional tenant culture defaults.
 *
 * Formatting methods mirror the pure functions from @zag/core/common
 * and are the recommended way to format values in TypeScript code.
 * Use the `zag*` pipes for Angular template formatting.
 */
@Injectable({
  providedIn: 'root',
})
export class FormattingService {
  private readonly languageService = inject(LanguageService);
  private readonly clientTimeZone = isPlatformBrowser(inject(PLATFORM_ID)) ? localTimeZone() : undefined;

  /**
   * The active formatting context, reactively derived from the current language signal.
   * Tenant defaults can be provided via `setTenantDefaults()`.
   */
  private readonly tenantDefaults = signal<TenantCultureDefaults | undefined>(undefined);

  readonly context = computed<FormattingContext>(() => {
    const tenantDefaults = this.tenantDefaults();
    return resolveFormattingContext(this.languageService.currentLanguage(), {
      ...tenantDefaults,
      timeZone: tenantDefaults?.timeZone ?? this.clientTimeZone,
    });
  });

  /**
   * Provide tenant culture defaults that will be applied as the second tier
   * of the resolution chain (user override → tenant defaults → system fallback).
   *
   * This should be called once during application initialization when tenant
   * configuration is available.
   */
  setTenantDefaults(defaults: TenantCultureDefaults): void {
    this.tenantDefaults.set(defaults);
  }

  /**
   * Formats a date value using the active locale and timezone.
   */
  formatDate(value: DateInput, overrides?: FormattingOverrides): string {
    return formatDate(value, this.context(), overrides);
  }

  /**
   * Formats a time value using the active locale and timezone.
   */
  formatTime(value: DateInput, overrides?: FormattingOverrides): string {
    return formatTime(value, this.context(), overrides);
  }

  /**
   * Formats a date-time value using the active locale and timezone.
   */
  formatDateTime(value: DateInput, overrides?: FormattingOverrides): string {
    return formatDateTime(value, this.context(), overrides);
  }

  /**
   * Formats a numeric value as currency using the active locale and currency code.
   */
  formatCurrency(value: number, overrides?: FormattingOverrides): string {
    return formatCurrency(value, this.context(), overrides);
  }

  /**
   * Formats a numeric value using the active locale.
   */
  formatNumber(
    value: number,
    overrides?: FormattingOverrides & { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ): string {
    return formatNumber(value, this.context(), overrides);
  }

  /**
   * Formats a decimal value (0–1) as a percent using the active locale.
   *
   * @param value - A decimal value between 0 and 1, e.g. 0.856 for 85.6%
   */
  formatPercent(
    value: number,
    overrides?: FormattingOverrides & { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ): string {
    return formatPercent(value, this.context(), overrides);
  }

  /**
   * Formats a date range using the active locale and timezone.
   */
  formatDateRange(start: DateInput, end: DateInput, overrides?: FormattingOverrides): string {
    return formatDateRange(start, end, this.context(), overrides);
  }

  /**
   * Formats a time range using the active locale and timezone.
   */
  formatTimeRange(start: DateInput, end: DateInput, overrides?: FormattingOverrides): string {
    return formatTimeRange(start, end, this.context(), overrides);
  }

  /**
   * General-purpose locale-aware date formatting with custom Intl.DateTimeFormatOptions.
   *
   * Use this for format patterns not covered by the dedicated formatDate/formatTime/etc. methods
   * (e.g. month-only, weekday-only, or month+year display strings).
   *
   * @example
   * // "February 2026" (en-CH)
   * this.formattingService.formatCustom(date, { month: 'long', year: 'numeric' })
   */
  formatCustom(value: DateInput, options: Intl.DateTimeFormatOptions, overrides?: FormattingOverrides): string {
    return formatCustom(value, this.context(), options, overrides);
  }

  /**
   * Returns the local wall-clock `HH:MM` of a UTC instant, suitable for
   * binding to `<input type="time">`.
   */
  formatTimeInputValue(value: DateInput, overrides?: FormattingOverrides): string {
    return formatTimeInputValue(value, this.context(), overrides);
  }

  /**
   * Returns the local wall-clock hour of a UTC instant as a fractional value
   * in `[0, 24)`. Used to position elements on a 24h time axis.
   */
  localHourFraction(value: DateInput, overrides?: FormattingOverrides): number | null {
    return localHourFraction(value, this.context(), overrides);
  }

  /**
   * Converts a local wall-clock date (`YYYY-MM-DD`) and time (`HH:MM`) in the
   * active timezone to a real-UTC ISO string.
   */
  wallClockToUtcIso(dateStr: string, time: string, overrides?: FormattingOverrides): string {
    return wallClockToUtcIso(dateStr, time, this.context(), overrides);
  }

  /**
   * Returns a short timezone abbreviation (e.g. "CET", "EST", "GMT+1")
   * derived from the active formatting context.
   */
  readonly timezoneAbbreviation = computed<string>(() => {
    const ctx = this.context();
    const formatted = formatCustom(new Date(), ctx, { timeZoneName: 'short' });
    // The timezone name is typically appended after a comma or space.
    // Use a regex to extract it from the formatted string.
    const match = formatted.match(/[A-Z]{2,5}[+-]?\d*$/);
    return match ? match[0] : ctx.timeZone;
  });
}
